/**
 * The responsive scale, for BOTH styling systems.
 *
 * Tailwind and MUI each do responsive in this app — hundreds of `md:`-style
 * prefixes against hundreds of responsive `sx` objects, and nine files using
 * both — and their scales used to disagree: `md` meant 768px in a className and
 * 900px in an `sx`, `lg` 1024 vs 1200. A layout written against one broke
 * inside the 132px band the other owned.
 *
 * So the numbers live here and both sides import them: `tailwind.config.ts` as
 * its `screens`, `createAppTheme` as MUI's `breakpoints.values`. Declared in a
 * module of its own rather than read out of the Tailwind config, because that
 * config pulls in the whole Tailwind colour package — fine for a build step,
 * not something to drag into the app bundle.
 *
 * The numbers are Tailwind's, and that is a MEASUREMENT, not a preference. The
 * switches that actually define a layout — `flex-direction`, `display`, widths,
 * grid columns — split 220 in Tailwind classes against 87 in responsive `sx`
 * objects, so ~2.5x as much structure was authored, and eyeballed, against 768
 * as against MUI's 900. The `sx` side is mostly padding and gaps, which move
 * harmlessly. Aligning the other way would have relocated the larger half.
 *
 * They are OURS now regardless of where they came from: this module is the one
 * place either system reads, so moving the tablet split (or adding a stop
 * between 768 and 1024, which the old MUI scale had at 900 and this one does
 * not) is a one-line decision here rather than a migration.
 *
 * `xs` has no Tailwind name — it is the base stop, below every prefix — so it
 * exists on the MUI side only.
 */
export const SCREENS = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const;

export type ScreenName = keyof typeof SCREENS;

/** The same scale as numbers, with MUI's base stop in front. */
export const BREAKPOINT_VALUES = {
  xs: 0,
  ...(Object.fromEntries(
    Object.entries(SCREENS).map(([name, width]) => [
      name,
      Number.parseInt(width, 10),
    ]),
  ) as Record<ScreenName, number>),
};
